import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAppAuth } from "../../../context/use-app-auth";
import requets from "../../../api";
import { convertValue } from "../../metrics/helpers/conversion-rules";

const PINS_KEY = (storeUuid) => `unifilar_pins_v1_${storeUuid}`;
const DIAGRAM_KEY = (storeUuid) => `unifilar_diagram_v1_${storeUuid}`;

function parseMetrics(data) {
  const measurements = Array.isArray(data?.measurements) ? data.measurements : [];
  return measurements
    .filter((m) => m.measurementUuid)
    .map((m) => {
      const { value, unit } = convertValue(m.value, m.unit);
      return { ...m, value: Number(value.toFixed(2)), unit };
    });
}

export default function useUnifilar() {
  const auth = useAppAuth();
  const req = useMemo(() => requets(auth.user?.access_token), [auth.user?.access_token]);

  const [stores, setStores] = useState([]);
  const [devices, setDevices] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [pins, setPins] = useState([]);
  const [diagramUrl, setDiagramUrl] = useState(null);
  const [deviceMetrics, setDeviceMetrics] = useState({});
  const [loading, setLoading] = useState(false);
  const loadedRef = useRef(new Set());

  // Load all locations (stores) once
  useEffect(() => {
    req
      .get("/locations")
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        setStores(list);
      })
      .catch(() => setStores([]));
  }, [req]);

  // Load ALL active devices once — no location filter
  // (location filter is unreliable if device<->location relation is not always populated)
  useEffect(() => {
    setLoading(true);
    req
      .get("/device")
      .then((res) => {
        const raw = res.data;
        const all = Array.isArray(raw) ? raw : (raw?.data || []);
        setDevices(all.filter((d) => d.isActive === true));
      })
      .catch(() => setDevices([]))
      .finally(() => setLoading(false));
  }, [req]);

  // When store changes: reload pins, diagram, clear metrics
  useEffect(() => {
    if (!selectedStore) {
      setPins([]);
      setDiagramUrl(null);
      setDeviceMetrics({});
      loadedRef.current = new Set();
      return;
    }
    const stored = localStorage.getItem(PINS_KEY(selectedStore.uuid));
    const loadedPins = stored ? JSON.parse(stored) : [];
    setPins(loadedPins);
    const diagram = localStorage.getItem(DIAGRAM_KEY(selectedStore.uuid));
    setDiagramUrl(diagram || null);
    setDeviceMetrics({});
    loadedRef.current = new Set();
  }, [selectedStore]);

  // Load metrics in parallel for all pins not yet fetched (async-parallel)
  useEffect(() => {
    if (pins.length === 0) return;
    const toLoad = pins.filter((p) => !loadedRef.current.has(p.devEui));
    if (toLoad.length === 0) return;
    toLoad.forEach((p) => loadedRef.current.add(p.devEui));

    Promise.all(
      toLoad.map((pin) =>
        req
          .get(`/metrics/${pin.devEui}/last`)
          .then((res) => ({ devEui: pin.devEui, metrics: parseMetrics(res.data) }))
          .catch(() => ({ devEui: pin.devEui, metrics: [] })),
      ),
    ).then((results) => {
      const updates = Object.fromEntries(results.map(({ devEui, metrics }) => [devEui, metrics]));
      setDeviceMetrics((prev) => ({ ...prev, ...updates }));
    });
  }, [pins, req]);

  const persistPins = useCallback(
    (newPins) => {
      if (!selectedStore) return;
      localStorage.setItem(PINS_KEY(selectedStore.uuid), JSON.stringify(newPins));
      setPins(newPins);
    },
    [selectedStore],
  );

  // Derive a Map for O(1) device lookup
  const deviceMap = useMemo(() => new Map(devices.map((d) => [d.devEui, d])), [devices]);

  const addPin = useCallback(
    (devEui, x, y) => {
      const device = deviceMap.get(devEui);
      if (!device) return;
      const newPin = {
        id: `${devEui}_${Date.now()}`,
        devEui,
        x,
        y,
        deviceName: device.name,
        storeName: selectedStore?.name || "",
        locationName: device.location?.name || device.storeName || "",
        isActive: device.isActive,
      };
      const newPins = [...pins, newPin];
      persistPins(newPins);
      loadedRef.current.add(devEui);
      req
        .get(`/metrics/${devEui}/last`)
        .then((res) => {
          setDeviceMetrics((prev) => ({ ...prev, [devEui]: parseMetrics(res.data) }));
        })
        .catch(() => {
          setDeviceMetrics((prev) => ({ ...prev, [devEui]: [] }));
        });
    },
    [pins, deviceMap, selectedStore, persistPins, req],
  );

  const removePin = useCallback(
    (pinId) => persistPins(pins.filter((p) => p.id !== pinId)),
    [pins, persistPins],
  );

  const updatePinPosition = useCallback(
    (pinId, x, y) => persistPins(pins.map((p) => (p.id === pinId ? { ...p, x, y } : p))),
    [pins, persistPins],
  );

  const saveDiagram = useCallback(
    (base64) => {
      if (!selectedStore) return;
      localStorage.setItem(DIAGRAM_KEY(selectedStore.uuid), base64);
      setDiagramUrl(base64);
    },
    [selectedStore],
  );

  const removeDiagram = useCallback(() => {
    if (!selectedStore) return;
    localStorage.removeItem(DIAGRAM_KEY(selectedStore.uuid));
    setDiagramUrl(null);
  }, [selectedStore]);

  return {
    stores,
    devices,
    selectedStore,
    setSelectedStore,
    pins,
    diagramUrl,
    deviceMetrics,
    loading,
    addPin,
    removePin,
    updatePinPosition,
    saveDiagram,
    removeDiagram,
  };
}
